import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, putData } from "../../../../utility/Utility";

function AddCommission() {
  const navigate = useNavigate();
  const location = useLocation();
  const editingCommission = location.state?.commission; // if updating
  const [policyType, setPolicyType] = useState(
    editingCommission?.policyType || ""
  );

  const [levels, setLevels] = useState(
    editingCommission?.levels?.map((lvl) => ({
      _id: lvl._id,
      level: lvl.level,
      percentage: lvl.percentage,
      requiredAgents: lvl.requiredAgents,
      designation: lvl.Designation || lvl.designation || "",
    })) || [{ level: 1, percentage: "", requiredAgents: "", designation: "" }]
  );

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [category, setCategoryId] = useState(
    editingCommission?.categoryId?._id || ""
  );
  const [subCategoryId, setSubCategoryId] = useState(
    editingCommission?.subCategoryId?._id || ""
  );
  const [hasSubCategories, setHasSubCategories] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasErrors, setHasErrors] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await postData("api/categories/list", {
          page: 1,
          limit: 100,
        });
        if (response?.status) setCategories(response?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Error fetching categories");
      }
    };
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (!category) {
      setSubCategories([]);
      setSubCategoryId("");
      setHasSubCategories(false);
      return;
    }
    const fetchSubCategories = async () => {
      try {
        const response = await postData("api/subcategory/list", {
          page: 1,
          limit: 100,
          category,
        });
        if (response?.status) {
          const subs = response?.data || [];
          setSubCategories(subs);
          setHasSubCategories(subs.length > 0);
        }
      } catch (error) {
        console.error(error);
        toast.error("Error fetching subcategories");
        setHasSubCategories(false);
      }
    };
    fetchSubCategories();
  }, [category]);

  // Validate whenever levels, policyType, or category change
  useEffect(() => {
    validateAllLevels();
  }, [levels, policyType, category]); // Added policyType and category as dependencies

  // Add / Remove Level - Maximum 6 levels
  const addLevel = () => {
    if (levels.length >= 6) {
      toast.warning("Maximum 6 levels allowed");
      return;
    }
    setLevels([
      ...levels,
      {
        level: levels.length + 1,
        percentage: "",
        requiredAgents: "",
        designation: "",
      },
    ]);
  };

  const removeLevel = (index) => {
    if (levels.length <= 1) {
      toast.warning("At least one level is required");
      return;
    }
    setLevels(levels.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedLevels = [...levels];

    if (field === "requiredAgents") {
      // Only allow numeric values, no negative numbers
      if (value === "") {
        updatedLevels[index][field] = "";
      } else {
        const numValue = Number(value.replace(/[^0-9]/g, ""));
        if (!isNaN(numValue) && numValue >= 0) {
          updatedLevels[index][field] = numValue;
        } else {
          return; // Don't update if invalid
        }
      }
    } else if (field === "percentage") {
      if (value === "") {
        updatedLevels[index][field] = "";
      } else {
        let num = Number(value);
        if (num < 0) num = 0;
        if (num > 100) num = 100;
        updatedLevels[index][field] = num;
      }
    } else {
      updatedLevels[index][field] = value;
    }

    setLevels(updatedLevels);
  };

  // Update policy type and trigger validation
  const handlePolicyTypeChange = (value) => {
    setPolicyType(value);
  };

  // Update category and trigger validation
  const handleCategoryChange = (value) => {
    setCategoryId(value);
    setSubCategoryId("");
  };

  // Validate all levels and set errors
  const validateAllLevels = () => {
    const errors = {};
    let hasAnyErrors = false;

    // Check for empty required fields
    for (let i = 0; i < levels.length; i++) {
      if (!levels[i].percentage && levels[i].percentage !== 0) {
        errors[`level_${i}_percentage`] = "Commission percentage is required";
        hasAnyErrors = true;
      }
      if (!levels[i].requiredAgents && levels[i].requiredAgents !== 0) {
        errors[`level_${i}_requiredAgents`] = "Required agents is required";
        hasAnyErrors = true;
      }
    }

    // Check required agents sequence
    for (let i = 1; i < levels.length; i++) {
      const currentAgents = Number(levels[i].requiredAgents);
      const previousAgents = Number(levels[i - 1].requiredAgents);

      if (currentAgents && previousAgents && currentAgents <= previousAgents) {
        errors[
          `level_${i}_sequence`
        ] = `Must be greater than ${previousAgents}`;
        hasAnyErrors = true;
      }
    }

    // Check basic required fields
    if (!policyType) {
      errors.policyType = "Policy type is required";
      hasAnyErrors = true;
    }
    if (!category) {
      errors.category = "Category is required";
      hasAnyErrors = true;
    }

    setFieldErrors(errors);
    setHasErrors(hasAnyErrors);
    return !hasAnyErrors;
  };

  // Submit handler (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAllLevels()) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    const payload = {
      policyType,
      levels: levels.map((lvl) => ({
        ...(lvl._id && { _id: lvl._id }),
        level: Number(lvl.level),
        percentage: Number(lvl.percentage),
        requiredAgents: Number(lvl.requiredAgents),
        Designation: lvl.designation,
      })),
      categoryId: category || undefined,
      subCategoryId: subCategoryId || undefined,
    };

    try {
      let response;
      if (editingCommission) {
        // Update
        payload.ruleId = editingCommission._id;
        response = await putData("api/refer-commission/update", payload);
      } else {
        // Create
        response = await postData("api/refer-commission/create", payload);
      }

      if (response?.success) {
        const successMsg =
          response?.message ||
          response?.msg ||
          (editingCommission
            ? "Updated successfully!"
            : "Created successfully!");

        toast.success(successMsg);
        navigate("/commission-list");
      } else {
        toast.error(
          response?.message || response?.msg || "Something went wrong"
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Error submitting data");
    }
  };

  // Prevent arrow keys and mouse wheel from changing number input
  const handleNumberInput = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  const handleWheel = (e) => {
    e.target.blur();
  };

  return (
    <div className="content-page">
      <ToastContainer />

      {/* Header Section */}
      <div className="content">
        <div className="container-fluid">
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                <div>
                  <h1 className="h3 mb-2 text-primary">
                    <i className="fas fa-percentage me-2"></i>
                    {editingCommission ? "Edit" : "Create"} Commission Rule
                  </h1>
                  <p className="text-muted mb-0">
                    {editingCommission
                      ? "Update the commission structure"
                      : "Define commission percentages for referral levels"}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                >
                  <i className="fas fa-arrow-left me-1"></i> Back
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="card border-0 shadow-sm">
            <div className="card-header bg-light py-3">
              <h2 className="h5 mb-0">
                <i className="fas fa-cog me-2 text-primary"></i>
                Commission Details
              </h2>
            </div>

            <div className="card-body p-4">
              {/* Basic Information Section */}
              <div className="mb-4">
                <h3 className="h6 mb-3 text-primary d-flex align-items-center">
                  <span className="badge bg-primary me-2">1</span>
                  Basic Information
                </h3>
                <div className="row g-3">
                  {/* Category Dropdown */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <i className="fas fa-folder me-2 text-muted"></i>
                      Category
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <select
                      className={`form-select ${fieldErrors.category ? "is-invalid" : ""}`}
                      value={category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      required
                    >
                      <option value="">-- Select a category --</option>
                      {categories.map((cat) => (
                        <option key={cat?._id} value={cat?._id}>
                          {cat?.title}
                        </option>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      Select the main category for this commission
                    </small>
                    {fieldErrors.category && (
                      <div className="invalid-feedback d-flex align-items-center">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {fieldErrors.category}
                      </div>
                    )}
                  </div>

                  {/* SubCategory Dropdown - Only show if category is selected AND has subcategories */}
                  {category && hasSubCategories && (
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-folder-open me-2 "></i>
                        SubCategory
                        <span className="text-muted"> (Optional)</span>
                      </label>
                      <select
                        className="form-select"
                        value={subCategoryId}
                        onChange={(e) => setSubCategoryId(e.target.value)}
                      >
                        <option value="">Select SubCategory</option>
                        {subCategories.map((sub) => (
                          <option key={sub._id} value={sub?._id}>
                            {sub?.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Policy Type Dropdown */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-file-contract me-2 "></i>
                      Policy Type
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${fieldErrors.policyType ? "is-invalid" : ""}`}
                      value={policyType}
                      onChange={(e) => handlePolicyTypeChange(e.target.value)}
                      required
                    >
                      <option value="">Select Policy Type</option>
                      <option value="fresh">Fresh</option>
                      <option value="renewal">Renewal</option>
                      <option value="port">Port</option>
                    </select>
                    {fieldErrors.policyType && (
                      <div className="invalid-feedback d-flex align-items-center">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {fieldErrors.policyType}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Commission Levels Section */}
              <div className="mb-5">
                <h3 className="h6 mb-3 text-primary">
                  <i className="fas fa-layer-group me-2"></i>
                  Commission Levels
                </h3>
                <div className="row g-3">
                  {levels?.map((lvl, index) => (
                    <div key={index} className="col-12">
                      <div className="card border-primary border-2 bg-light">
                        <div className="card-header bg-primary bg-opacity-10 py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0  fw-bold">
                              <i className="fas fa-level-up-alt me-2"></i>
                              Level {lvl.level}
                            </h6>
                            {levels.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeLevel(index)}
                              >
                                <i className="fas fa-trash me-1"></i> Remove
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="card-body p-3">
                          <div className="row g-3 align-items-end">
                            {/* Level Number */}
                            <div className="col-12 col-sm-6 col-md-3">
                              <label className="form-label fw-semibold">
                                Level Number
                              </label>
                              <input
                                type="number"
                                className="form-control border-primary"
                                value={lvl.level}
                                onChange={(e) =>
                                  handleChange(index, "level", e.target.value)
                                }
                                onKeyDown={handleNumberInput}
                                onWheel={handleWheel}
                                min="1"
                                required
                              />
                              <small className="text-muted">Level</small>
                            </div>

                            {/* Percentage */}
                            <div className="col-12 col-sm-6 col-md-3">
                              <label className="form-label fw-semibold">
                                Commission Percentage (%)
                                <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className={`form-control ${
                                    fieldErrors[`level_${index}_percentage`]
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={lvl.percentage}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    if (value === "") {
                                      handleChange(index, "percentage", "");
                                      return;
                                    }
                                    let num = Number(value);
                                    if (num < 0) num = 0;
                                    if (num > 100) num = 100;
                                    handleChange(index, "percentage", num);
                                  }}
                                  onKeyDown={handleNumberInput}
                                  onWheel={handleWheel}
                                  required
                                />
                                <span className="input-group-text bg-primary text-white">
                                  %
                                </span>
                              </div>
                              {fieldErrors[`level_${index}_percentage`] ? (
                                <div className="invalid-feedback d-flex align-items-center">
                                  <i className="fas fa-exclamation-circle me-2"></i>
                                  {fieldErrors[`level_${index}_percentage`]}
                                </div>
                              ) : (
                                <small className="text-muted">
                                  0 - 100% range
                                </small>
                              )}
                            </div>

                            {/* Required Agents */}
                            <div className="col-12 col-sm-6 col-md-4">
                              <label className="form-label fw-semibold">
                                Total Required Agents
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${
                                  fieldErrors[
                                    `level_${index}_requiredAgents`
                                  ] || fieldErrors[`level_${index}_sequence`]
                                    ? "is-invalid"
                                    : ""
                                }`}
                                value={lvl.requiredAgents}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  handleChange(
                                    index,
                                    "requiredAgents",
                                    value
                                  );
                                }}
                                onKeyDown={handleNumberInput}
                                onWheel={handleWheel}
                                placeholder="Enter number of agents"
                                required
                              />
                              {fieldErrors[`level_${index}_sequence`] ? (
                                <div className="invalid-feedback d-flex align-items-center">
                                  <i className="fas fa-exclamation-circle me-2"></i>
                                  {fieldErrors[`level_${index}_sequence`]}
                                </div>
                              ) : fieldErrors[
                                  `level_${index}_requiredAgents`
                                ] ? (
                                <div className="invalid-feedback d-flex align-items-center">
                                  <i className="fas fa-exclamation-circle me-2"></i>
                                  {
                                    fieldErrors[
                                      `level_${index}_requiredAgents`
                                    ]
                                  }
                                </div>
                              ) : index > 0 &&
                                levels[index - 1].requiredAgents ? (
                                <small className="text-info fw-semibold">
                                  <i className="fas fa-info-circle me-1"></i>
                                  Must be greater than{" "}
                                  {levels[index - 1].requiredAgents}
                                </small>
                              ) : index === 0 ? (
                                <small className="text-muted">
                                  Minimum required agents for this level
                                </small>
                              ) : null}
                            </div>

                            {/* Remove Button for Mobile */}
                            <div className="col-12 col-sm-6 col-md-2 d-block d-md-none">
                              {levels.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger w-100"
                                  onClick={() => removeLevel(index)}
                                >
                                  <i className="fas fa-trash me-1"></i> Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Level Button */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addLevel}
                    disabled={levels.length >= 6}
                  >
                    <i className="fas fa-plus-circle me-2"></i>
                    {levels.length >= 6
                      ? "Maximum Levels Reached"
                      : "Add New Level"}
                  </button>
                  {levels.length >= 6 && (
                    <div className="mt-2">
                      <span className="badge bg-danger fs-6">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Maximum 6 levels reached
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="card-footer bg-light py-4">
              <div className="d-flex justify-content-end gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/commission-list")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={hasErrors}
                >
                  {hasErrors ? (
                    <>
                      <i className="fas fa-exclamation-circle me-2"></i>
                      Fix Errors to Save
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      {editingCommission ? "Update" : "Save"} Commission
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddCommission;
